import React, { useState, useEffect, useRef, useContext } from 'react';
import axios from '../../../../config/axios';
import UserContext from '../../../../contexts/UserContext';
import { InputDiv, Label, Input, SearchOptions, Option } from '../../../../components/styles/select-style';

interface ItemType {
  id: number;
}

interface VendorSelectProps<T extends ItemType> {
  label: string;
  apiUrl: string;
  displayField: keyof T;
  value: T;
  onChange: (item: T) => void;
}

const VendorSelect = <T extends ItemType>({
  label,
  apiUrl,
  displayField,
  value,
  onChange,
}: VendorSelectProps<T>) => {
  const { user } = useContext(UserContext);
  const [items, setItems] = useState<T[]>([]);
  const [allSellers, setAllSellers] = useState<T[]>([]);
  const getDisplayName = (val: any): string => {
    if (!val) return '';
    return (val[displayField] || val.name || val.company || '') as string;
  };

  const [inputItem, setInputItem] = useState<string>(getDisplayName(value));
  const [showOptions, setShowOptions] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const optionsRef = useRef<HTMLUListElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const valueId = value.id;
  const valueName = getDisplayName(value);
  const valueEmail = (value as any).email;


  useEffect(() => {
    if (valueName) {
      setInputItem(valueName);
    } else if (valueId && allSellers.length > 0) {
      const found = allSellers.find((item) => item.id === valueId);
      setInputItem(found ? getDisplayName(found) : '');
    } else {
      setInputItem('');
    }
  }, [valueId, valueName, displayField, allSellers]);

  useEffect(() => {
    if (valueId && allSellers.length > 0) {
      const found = allSellers.find(item => item.id === valueId);
      if (found) {
        const hasMissingDetails = ('email' in found && !valueEmail);
        if (hasMissingDetails) {
          onChange(found);
        }
      }
    }
  }, [valueId, allSellers, valueEmail, onChange]);

  useEffect(() => {
    const fetchSellers = async () => {
      try {
        const response = await axios.get<any>(apiUrl, {
          params: { loadQuantity: 1000 },
        });

        const dataArray = Array.isArray(response.data)
          ? response.data
          : (response.data?.data || []);

        setAllSellers(dataArray);
        setItems(dataArray);
      } catch (error) {
        console.error("Erro ao buscar dados:", error);
        setAllSellers([]);
        setItems([]);
      }
    };
    fetchSellers();
  }, [apiUrl]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        optionsRef.current &&
        inputRef.current &&
        !optionsRef.current.contains(target) &&
        !inputRef.current.contains(target)
      ) {
        setShowOptions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputItem(newValue);

    if (newValue === '') {
      setItems(allSellers);
    } else {
      const filterCriteria = newValue.toLowerCase();
      const filteredItems = allSellers.filter(item =>
        (item[displayField] as string)?.toLowerCase()?.includes(filterCriteria)
      );
      setItems(filteredItems);
    }

    setSelectedIndex(0);
    onChange({ id: 0, [displayField]: newValue } as unknown as T);
    setShowOptions(true);
  };

  const handleSelectItem = (item: T) => {
    setInputItem(item[displayField] as string);
    setShowOptions(false);
    onChange(item);
  };

  const handleSetOptionByKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' && selectedIndex < items.length - 1) {
      setSelectedIndex(prev => prev + 1);
      e.preventDefault();
    }
    if (e.key === 'ArrowUp' && selectedIndex > 0) {
      setSelectedIndex(prev => prev - 1);
      e.preventDefault();
    }
    if (e.key === 'Enter') {
      if (items[selectedIndex]) {
        handleSelectItem(items[selectedIndex]);
      }
      e.preventDefault();
    }
    if (e.key === 'Tab') {
      setShowOptions(false);
    }
  };

  return (
    <InputDiv>
      <Label>{label}</Label>
      <Input
        ref={inputRef}
        type='text'
        placeholder='Selecione'
        value={inputItem}
        onFocus={() => setShowOptions(true)}
        onChange={handleChangeInput}
        onKeyDown={handleSetOptionByKey}
      />
      {showOptions && items && items.length > 0 &&
        <SearchOptions ref={optionsRef}>
          {items.map((s, index) =>
            <Option
              key={s.id}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleSelectItem(s)}
              isSelected={index === selectedIndex}
            >
              {s[displayField] as React.ReactNode}
            </Option>
          )}
        </SearchOptions>
      }
    </InputDiv>
  );
};

export default VendorSelect;